/**
 * CADModelList - List of uploaded CAD models
 */

import React, { useMemo, useState } from 'react';
import {
  Box as ModelIcon,
  Trash2 as DeleteIcon,
  CircleCheck as CompletedIcon,
  CircleX as ErrorIcon,
  Hourglass as PendingIcon,
  Columns2 as CompareIcon,
  Folder,
  Search,
  Tags,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CADModel } from '../../types/cad';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import {
  classificationOrEmpty,
  normalizeClassificationSearch,
} from './classification';

interface CADModelListProps {
  models: CADModel[];
  selectedModelId: string | null;
  onSelect: (model: CADModel) => void;
  onDelete?: (modelId: string) => void;
  onSetComparisonTarget?: (model: CADModel) => void;
  comparisonModelId?: string | null;
  onEditClassification?: (model: CADModel) => void;
}

const ALL_FOLDERS = '__all__';
const UNCLASSIFIED_FOLDER = '__unclassified__';
const FOLDER_VALUE_PREFIX = 'folder:';

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CompletedIcon className="h-4 w-4 text-success" />;
    case 'failed':
      return <ErrorIcon className="h-4 w-4 text-destructive" />;
    default:
      return <PendingIcon className="h-4 w-4 text-muted-foreground" />;
  }
}

function formatDate(isoString: string, language: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(language.startsWith('en') ? 'en-US' : 'ja-JP', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CADModelList: React.FC<CADModelListProps> = ({
  models,
  selectedModelId,
  onSelect,
  onDelete,
  onSetComparisonTarget,
  comparisonModelId,
  onEditClassification,
}) => {
  const { i18n, t } = useTranslation();
  const [query, setQuery] = useState('');
  const [folderFilter, setFolderFilter] = useState(ALL_FOLDERS);

  const folderOptions = useMemo(() => (
    Array.from(new Set(models.map((model) => (
      classificationOrEmpty(model.classification).folderPath
    )).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right, i18n.language))
  ), [i18n.language, models]);
  const activeFolderFilter = folderFilter.startsWith(FOLDER_VALUE_PREFIX)
    && !folderOptions.includes(folderFilter.slice(FOLDER_VALUE_PREFIX.length))
    ? ALL_FOLDERS
    : folderFilter;
  const normalizedQuery = normalizeClassificationSearch(query);
  const filteredModels = useMemo(() => models.filter((model) => {
    const classification = classificationOrEmpty(model.classification);
    const folderMatches = activeFolderFilter === ALL_FOLDERS
      || (activeFolderFilter === UNCLASSIFIED_FOLDER
        ? !classification.folderPath
        : classification.folderPath === activeFolderFilter.slice(FOLDER_VALUE_PREFIX.length));
    if (!folderMatches) return false;
    if (!normalizedQuery) return true;
    return [
      model.filename,
      classification.folderPath,
      classification.machineModel,
      classification.managementNumber,
      ...classification.tags,
    ].some((value) => normalizeClassificationSearch(value).includes(normalizedQuery));
  }), [activeFolderFilter, models, normalizedQuery]);
  const groupedModels = useMemo(() => {
    const groups = new Map<string, CADModel[]>();
    filteredModels.forEach((model) => {
      const folder = classificationOrEmpty(model.classification).folderPath
        || UNCLASSIFIED_FOLDER;
      const existing = groups.get(folder);
      if (existing) existing.push(model);
      else groups.set(folder, [model]);
    });
    return Array.from(groups.entries()).sort(([left], [right]) => {
      if (left === UNCLASSIFIED_FOLDER) return 1;
      if (right === UNCLASSIFIED_FOLDER) return -1;
      return left.localeCompare(right, i18n.language);
    });
  }, [filteredModels, i18n.language]);

  if (models.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-muted-foreground">{t('cad.modelList.emptyTitle')}</p>
        <p className="text-xs text-muted-foreground/70">{t('cad.modelList.emptyDescription')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-1.5 p-1 pb-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label={t('cad.modelList.searchLabel')}
            placeholder={t('cad.modelList.searchPlaceholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-8 pl-7 text-xs"
          />
        </div>
        <label className="sr-only" htmlFor="cad-folder-filter">
          {t('cad.modelList.folderFilter')}
        </label>
        <select
          id="cad-folder-filter"
          value={activeFolderFilter}
          onChange={(event) => setFolderFilter(event.target.value)}
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value={ALL_FOLDERS}>{t('cad.modelList.allFolders')}</option>
          {folderOptions.map((folder) => (
            <option key={folder} value={`${FOLDER_VALUE_PREFIX}${folder}`}>{folder}</option>
          ))}
          <option value={UNCLASSIFIED_FOLDER}>{t('cad.modelList.unclassified')}</option>
        </select>
      </div>
      {filteredModels.length === 0 && (
        <p className="rounded-md bg-muted p-2 text-xs text-muted-foreground">
          {t('cad.modelList.noResults')}
        </p>
      )}
      {groupedModels.map(([folder, group]) => (
        <section key={folder} aria-label={folder === UNCLASSIFIED_FOLDER ? t('cad.modelList.unclassified') : folder}>
          <div className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-muted-foreground">
            <Folder className="size-3" />
            <span className="truncate">
              {folder === UNCLASSIFIED_FOLDER ? t('cad.modelList.unclassified') : folder}
            </span>
            <span>({group.length})</span>
          </div>
          <ul className="m-0 list-none p-0">
            {group.map((model) => {
              const classification = classificationOrEmpty(model.classification);
              return (
                <li key={model.id} className="relative mb-1">
          <button
            type="button"
            onClick={() => onSelect(model)}
            aria-current={model.id === selectedModelId || undefined}
            className={cn(
              'w-full flex items-center gap-2 rounded-md px-2 py-1.5 pr-24 text-left hover:bg-accent',
              model.id === selectedModelId && 'bg-accent',
            )}
          >
            <ModelIcon
              className={cn(
                'h-4 w-4 shrink-0',
                model.id === selectedModelId ? 'text-primary' : 'text-muted-foreground',
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm truncate max-w-40">{model.filename}</p>
              <div className="flex items-center gap-1 mt-0.5">
                {getStatusIcon(model.status)}
                <span className="text-xs text-muted-foreground/70">
                  {formatDate(model.createdAt, i18n.language)}
                </span>
              </div>
              {(classification.machineModel || classification.managementNumber) && (
                <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                  {[classification.machineModel, classification.managementNumber]
                    .filter(Boolean).join(' / ')}
                </p>
              )}
              {classification.tags.length > 0 && (
                <div className="mt-1 flex min-w-0 gap-1 overflow-hidden">
                  {classification.tags.slice(0, 2).map((tag) => (
                    <Badge key={tag} variant="outline" className="max-w-20 truncate px-1 py-0 text-[10px]">
                      {tag}
                    </Badge>
                  ))}
                  {classification.tags.length > 2 && (
                    <span className="text-[10px] text-muted-foreground">
                      +{classification.tags.length - 2}
                    </span>
                  )}
                </div>
              )}
            </div>
          </button>
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
            {onSetComparisonTarget && model.id !== selectedModelId && model.status === 'completed' && (
              <TooltipIconButton
                label={t('cad.modelList.setComparisonTarget')}
                icon={<CompareIcon className="h-4 w-4" />}
                size="icon"
                className={cn('h-7 w-7', model.id === comparisonModelId && 'text-primary')}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetComparisonTarget(model);
                }}
              />
            )}
            {onDelete && (
              <TooltipIconButton
                label={t('cad.modelList.delete')}
                icon={<DeleteIcon className="h-4 w-4" />}
                size="icon"
                className="h-7 w-7"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(model.id);
                }}
              />
            )}
            {onEditClassification && model.canEditClassification && (
              <TooltipIconButton
                label={t('cad.modelList.editClassification')}
                icon={<Tags className="h-4 w-4" />}
                size="icon"
                className="h-7 w-7"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditClassification(model);
                }}
              />
            )}
          </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default CADModelList;
